/**
 * CUSTOMER-R4.15-K13 — canonical Help / Feedback entry + bottom sheet.
 * One compact launcher in the app chrome. Not two large buttons on every page.
 */

import { useEffect, useState } from "react";
import { emitEvent } from "./events-v2.js";
import {
  buildFeedbackDiagnosticEnvelope,
  requestCustomerFeedback,
  subscribeCustomerFeedbackOpen,
  type CustomerFeedbackType,
} from "./feedback-diagnostics-v2.js";
import { StoreV2Icon } from "./StoreV2Icon.js";
import { t, type StoreV2CopyKey } from "./store-v2-ui-copy.js";
import type { StoreV2Lang } from "./store-v2-locale.js";

function readTelegramUserId(): string | null {
  try {
    const wa = (window as unknown as { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number } } } } })
      .Telegram?.WebApp;
    const id = wa?.initDataUnsafe?.user?.id;
    return id != null ? String(id).slice(0, 32) : null;
  } catch {
    return null;
  }
}

export function CustomerFeedbackLauncher({ lang }: { lang: StoreV2Lang }) {
  return (
    <button
      type="button"
      className="sv2-navbtn sv2-feedback-launcher"
      data-testid="sv2-feedback-launcher"
      aria-label={t(lang, "help_feedback")}
      title={t(lang, "help_feedback")}
      onClick={() => {
        emitEvent("feedback_opened", { origin: "header" });
        emitEvent("cta_click", { target_key: "feedback.open" });
        requestCustomerFeedback("chooser");
      }}
    >
      <StoreV2Icon name="help" size={20} />
    </button>
  );
}

export function ReportErrorCta({ lang, origin }: { lang: StoreV2Lang; origin: string }) {
  return (
    <button
      type="button"
      className="sv2-cta sv2-secondary sv2-report-error-cta"
      data-testid="sv2-report-error-cta"
      onClick={() => {
        emitEvent("feedback_opened", { origin, type: "BUG" });
        emitEvent("cta_click", { target_key: "feedback.bug" });
        requestCustomerFeedback("BUG");
      }}
    >
      {t(lang, "report_bug")}
    </button>
  );
}

export function CustomerFeedbackHost({ lang }: { lang: StoreV2Lang }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"chooser" | CustomerFeedbackType>("chooser");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [doneId, setDoneId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeCustomerFeedbackOpen(({ type }) => {
      setMode(type);
      setComment("");
      setDoneId(null);
      setError(null);
      setOpen(true);
    });
  }, []);

  if (!open) return null;

  const close = () => {
    if (busy) return;
    setOpen(false);
  };

  const submit = async (type: CustomerFeedbackType) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const envelope = buildFeedbackDiagnosticEnvelope(type);
    try {
      const res = await fetch("/api/store-v2/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          comment: comment.trim().slice(0, 800),
          telegram_user_id: readTelegramUserId(),
          diagnostics: type === "BUG" ? envelope : {
            route: envelope.route,
            source_route: envelope.source_route,
            build_id: envelope.build_id,
            timestamp: envelope.timestamp,
            public_order_number: envelope.public_order_number,
          },
        }),
        keepalive: true,
      });
      const json = (await res.json().catch(() => ({}))) as { ok?: boolean; feedback_id?: string; error?: string };
      if (json.ok && json.feedback_id) {
        emitEvent("feedback_submitted", { type, feedback_id: json.feedback_id });
        emitEvent("cta_click", { target_key: type === "BUG" ? "feedback.bug" : "feedback.improvement" });
        setDoneId(json.feedback_id);
      } else {
        setError(t(lang, "feedback_send_fail"));
      }
    } catch {
      setError(t(lang, "feedback_send_fail"));
    } finally {
      setBusy(false);
    }
  };

  const titleKey: StoreV2CopyKey =
    mode === "BUG" ? "report_bug" : mode === "IMPROVEMENT" ? "suggest_improvement" : "help_feedback";

  return (
    <div className="sv2-sheet-overlay" data-testid="sv2-feedback-sheet" role="dialog" aria-modal="true">
      <button type="button" className="sv2-sheet-backdrop" aria-label={t(lang, "close")} onClick={close} />
      <div className="sv2-sheet-panel sv2-feedback-sheet">
        <div className="sv2-sheet-handle" aria-hidden="true" />
        <h3 className="sv2-feedback-title">{t(lang, titleKey)}</h3>
        {doneId ? (
          <>
            <p className="sv2-muted">{t(lang, "feedback_thanks")}</p>
            <p className="sv2-muted" data-testid="sv2-feedback-id" style={{ fontSize: 12 }}>
              ID: {doneId}
            </p>
            <button type="button" className="sv2-cta" onClick={close}>
              {t(lang, "close")}
            </button>
          </>
        ) : mode === "chooser" ? (
          <div className="sv2-feedback-chooser">
            <button
              type="button"
              className="sv2-cta sv2-feedback-choice"
              data-testid="sv2-feedback-choose-bug"
              onClick={() => setMode("BUG")}
            >
              {t(lang, "report_bug")}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-secondary sv2-feedback-choice"
              data-testid="sv2-feedback-choose-improvement"
              onClick={() => setMode("IMPROVEMENT")}
            >
              {t(lang, "suggest_improvement")}
            </button>
          </div>
        ) : (
          <>
            <p className="sv2-muted" style={{ fontSize: 13 }}>
              {mode === "BUG" ? t(lang, "feedback_bug_hint") : t(lang, "feedback_improve_hint")}
            </p>
            <textarea
              className="sv2-feedback-comment"
              data-testid="sv2-feedback-comment"
              rows={4}
              maxLength={800}
              value={comment}
              placeholder={t(lang, "feedback_comment_placeholder")}
              onChange={(e) => setComment(e.target.value)}
            />
            {error ? <p className="sv2-field-err">{error}</p> : null}
            <button
              type="button"
              className="sv2-cta"
              data-testid="sv2-feedback-submit"
              disabled={busy || (mode === "IMPROVEMENT" && !comment.trim())}
              onClick={() => void submit(mode)}
            >
              {busy ? t(lang, "submitting") : t(lang, "feedback_send")}
            </button>
            <button type="button" className="sv2-cta sv2-secondary" disabled={busy} onClick={() => setMode("chooser")}>
              {t(lang, "back")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
